import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import type { CorpusFeed } from "@shared/types/corpus/api.ts";
import { CorpusFeedView } from "../../../../../lib/corpusFeedView";
import { gaps } from "../../../../../components/primitives/spacing";
import { styled } from "../../../../../lib/styled";
import { useUrlFilters } from "../../../../../lib/url_filters";
import { FEED_TABS, feedDetailFiltersSchema } from "../feedDetailFilters";
import type { UseFeedDetailResult } from "../useFeedDetail";
import { FeedHeader } from "./FeedHeader";
import { FeedMetrics } from "./FeedMetrics";
import { FeedPapers } from "./FeedPapers";
import { FeedQuery } from "./FeedQuery";
import { FeedRuns } from "./FeedRuns";

// A little room around the feed, so its cards do not sit against the edge of the screen.
const Content = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1),
}));

interface FeedBodyProps {
  feed: CorpusFeed;
  detail: UseFeedDetailResult;
}

/** The page of a feed that has loaded: who it is and its numbers, then, on tabs, its papers and
 *  query (Overview) or its runs. Each part reads only what it shows; what an admin can do to the feed is in the page's header. */
export function FeedBody({ feed, detail }: FeedBodyProps) {
  const statistics = detail.statistics.byFeed[feed.id];
  const view = new CorpusFeedView(feed, statistics);
  const [{ tab, composition: compositionTab }, setFilters] = useUrlFilters(feedDetailFiltersSchema);

  return (
    <Content gap={gaps.base}>
      <FeedHeader
        view={view}
        onSaveDescription={(description) => detail.saveDetails({ description })}
      />
      <FeedMetrics view={view} />

      <Tabs
        value={tab}
        onChange={(_event, value: typeof tab) =>
          setFilters({ tab: value, composition: compositionTab })
        }
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        {FEED_TABS.map(({ value, label }) => (
          <Tab key={value} value={value} label={label} />
        ))}
      </Tabs>

      {tab === "overview" ? (
        <Grid container spacing={gaps.base}>
          <Grid size={{ xs: 12, md: 7 }}>
            <FeedPapers
              statistics={statistics}
              unavailable={detail.statistics.unavailable.has(feed.id)}
              composition={detail.composition}
              tab={compositionTab}
              onTabChange={(composition) => setFilters({ tab, composition })}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <FeedQuery query={feed.query} />
          </Grid>
        </Grid>
      ) : (
        <FeedRuns runs={detail.runs} />
      )}
    </Content>
  );
}
